import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  closeTicket,
  getTicket,
  reopenTicket,
  resolveTicket,
  startTicket,
  type TicketDetail,
} from "../../../api/tickets";
import { listTicketFiles, type TicketFile } from "../../../api/files";
import TicketHeader from "./components/TicketHeader";
import TicketSummary from "./components/TicketSummary";
import TicketAttachments from "./components/TicketAttachments";

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const id = ticketId ? Number(ticketId) : NaN;
  const ATTACHMENT_TIMEOUT_MS = 8000;

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [attachments, setAttachments] = useState<TicketFile[]>([]);
  const [activities] = useState([]); // placeholder until activity feed exists
  const [loading, setLoading] = useState(true);
  const [attachmentsLoading, setAttachmentsLoading] = useState(true);

  const loadAttachments = async (ticketIdValue: number) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), ATTACHMENT_TIMEOUT_MS);
    try {
      const files = await listTicketFiles(ticketIdValue, controller.signal);
      setAttachments(Array.isArray(files) ? files : []);
    } catch (filesErr: any) {
      if (filesErr?.name === "AbortError") {
        console.warn(`Ticket attachments request timed out after ${ATTACHMENT_TIMEOUT_MS}ms`);
      } else {
        console.error("Failed to load attachments", filesErr);
      }
    } finally {
      clearTimeout(timeout);
      setAttachmentsLoading(false);
    }
  };

  const loadTicket = async () => {
    if (!Number.isFinite(id)) return;
    try {
      setLoading(true);
      setAttachmentsLoading(true);
      // Load attachments in parallel so a slow/failed MinIO link doesn't block the ticket details.
      void loadAttachments(id);
      const t = await getTicket(id);
      setTicket(t);
    } catch (err) {
      console.error("Failed to load ticket", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const handleStatusChange = async (action: "start" | "resolve" | "close" | "reopen") => {
    if (!ticket) return;
    try {
      if (action === "start") await startTicket(ticket.id);
      if (action === "resolve") await resolveTicket(ticket.id);
      if (action === "close") await closeTicket(ticket.id);
      if (action === "reopen") await reopenTicket(ticket.id);
      await loadTicket();
    } catch (err) {
      console.error("Failed to update ticket status", err);
    }
  };

  if (loading || !ticket) {
    return <div className="p-6 text-slate-600">Loading ticket...</div>;
  }

  const summaryData = {
    siteName: (ticket as any).siteName ?? ticket.metrics?.site_name ?? `Site ${ticket.site_id}`,
    arrayName: (ticket as any).arrayName ?? ticket.metrics?.array_name ?? (ticket.array_id ? `Array ${ticket.array_id}` : "—"),
    inverterName:
      (ticket as any).inverterName ??
      ticket.metrics?.inverter_name ??
      (ticket.array_inverter_id ? `Inverter ${ticket.array_inverter_id}` : "—"),
    notes: ticket.notes,
    diagnosis_json: ticket.diagnosis_json,
  };

  return (
    <div className="p-6 space-y-6">
      <TicketHeader
        ticket={ticket}
        onStartWork={() => handleStatusChange("start")}
        onResolve={() => handleStatusChange("resolve")}
        onClose={() => handleStatusChange("close")}
        onReopen={() => handleStatusChange("reopen")}
      />

      <TicketSummary ticket={summaryData} />

      <TicketAttachments
        ticketId={ticket.id}
        attachments={attachments}
        loading={attachmentsLoading}
        onRefresh={loadTicket}
      />

      <section className="bg-white shadow-sm rounded-lg border border-slate-200">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Activity Log</h2>
          <span className="text-xs uppercase text-slate-500">Placeholder</span>
        </header>
        <ol className="divide-y divide-slate-200">
          {activities.map((item) => (
            <li key={item.id} className="px-4 py-3 flex items-start gap-3">
              <div className="mt-1 h-2 w-2 rounded-full bg-brand-500" />
              <div>
                <div className="text-sm text-slate-900">{item.label}</div>
                <div className="text-xs text-slate-500">
                  {item.actor ? `${item.actor} • ` : ""}
                  {new Date(item.at).toLocaleString()}
                </div>
              </div>
            </li>
          ))}
          {!activities.length && (
            <li className="px-4 py-3 text-sm text-slate-500">No activity yet. Events will appear here.</li>
          )}
        </ol>
      </section>
    </div>
  );
}
